import React, {createContext, useContext, useEffect, useState, useCallback, useMemo} from "react";
import {fetchPortfolio} from "../services/api";
import {
  greeting as cfgGreeting,
  socialMediaLinks as cfgSocial,
  skillsSection as cfgSkills,
  educationInfo as cfgEducation,
  techStack as cfgTechStack,
  workExperiences as cfgExperience,
  bigProjects as cfgProjects,
  certifications as cfgCertifications,
  contactInfo as cfgContact,
  blogHeroContent as cfgBlogHero,
} from "@features/portfolio/config";

// The static config.js is the source of truth for DEFAULTS. The DB document
// (admin-edited) overrides it section by section. If the DB is empty or the API
// is down, every section still renders from these defaults — the site never
// breaks. This mirrors SettingsContext's fail-open philosophy.

const mergeObj = (base, over) =>
  ({...base, ...(over && typeof over === "object" && !Array.isArray(over) ? over : {})});

// Build the merged content tree from a (possibly null) DB document.
const buildContent = (db) => {
  const d = db && typeof db === "object" ? db : {};
  return {
    greeting:   mergeObj(cfgGreeting, d.greeting),
    social:     mergeObj(cfgSocial, d.social),
    skills:     mergeObj(cfgSkills, d.skills),
    education:  mergeObj(cfgEducation, d.education),
    techStack:  mergeObj(cfgTechStack, d.techStack),
    projects:   mergeObj(cfgProjects, d.projects),
    experience: mergeObj(cfgExperience, d.experience),
    contact:    mergeObj(cfgContact, d.contact),
    blogHero:   mergeObj(cfgBlogHero, d.blogHero),
    // certifications has a real static default (cfgCertifications, config.js)
    // so the badges section renders even before the DB loads or if the admin
    // hasn't touched it — same "never blank" rule as bigProjects. Once the
    // admin saves their own list (even an edited one) via Portfolio >
    // Certifications, that DB list takes over.
    certifications: Array.isArray(d.certifications) && d.certifications.length
      ? d.certifications
      : cfgCertifications,
    // New, DB-only section (no config default) — empty until the admin adds them.
    testimonials:   Array.isArray(d.testimonials) ? d.testimonials : [],
    featuredAI:     d.featuredAI && typeof d.featuredAI === "object" ? d.featuredAI : null,
    seo:            d.seo && typeof d.seo === "object" ? d.seo : {},
  };
};

const DEFAULT_CONTENT = buildContent(null);

const PortfolioContext = createContext({
  content: DEFAULT_CONTENT,
  loaded: false,
  offline: false,
  refresh: () => {},
});

export const PortfolioProvider = ({children}) => {
  // Start from defaults so the FIRST paint is already a complete site.
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [loaded, setLoaded]   = useState(false);
  const [offline, setOffline] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const res = await fetchPortfolio();      // never throws
      setContent(buildContent(res?.portfolio));
      setOffline(!!res?.offline);
    } catch (err) {
      // Defensive: anything unexpected keeps defaults so the tree never crashes.
      // eslint-disable-next-line no-console
      console.warn("[PortfolioContext] using static defaults:", err?.message);
      setContent(DEFAULT_CONTENT);
      setOffline(true);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const value = useMemo(() => ({content, loaded, offline, refresh}), [content, loaded, offline, refresh]);

  return <PortfolioContext.Provider value={value}>{children}</PortfolioContext.Provider>;
};

// Convenience hook returning the merged content tree directly.
export const usePortfolio = () => useContext(PortfolioContext).content;
export const usePortfolioMeta = () => useContext(PortfolioContext);

export default PortfolioContext;
